import { findNode, viaOptions } from "../core/sim";
import type { ReplayAction } from "../core/replay";
import { hitNode, hitRoute, MapRenderer, NODE_HIT_R, ROUTE_HIT_R } from "../render/canvas";
import { panBy, zoomAround } from "../render/view";
import type { GameState } from "../core/types";
import { coarsePointer } from "./format";
import type { Hud } from "./hud";
import type { Panel } from "./panel";
import type { UiState } from "./state";

export interface InputDeps {
  canvas: HTMLCanvasElement;
  renderer: MapRenderer;
  /** Getter, weil beim Neustart ein neues State-Objekt entsteht. */
  getState: () => GameState;
  ui: UiState;
  panel: Panel;
  hud: Hud;
  runCommand: (action: ReplayAction) => boolean;
}

/** How far a pointer may travel and still count as a tap, in CSS px. */
const TAP_SLOP = 8;
/**
 * Half the 44 px target floor, in CSS px. A finger gets a hit radius at least
 * this big whatever the camera is showing.
 *
 * Without it the map is only operable after a pinch: at the fit scale of a
 * 390 px phone, NODE_HIT_R of 33 world units is 13 px on screen, so the primary
 * gesture of the game — tap a producer, tap a city — needs a zoom first. The
 * targets grow instead of the player working around them.
 */
const TOUCH_TARGET_R = 22;
/** How long it may stay down, in ms. Longer is a considered drag, not a tap. */
const TAP_TIME = 600;

interface Tracked {
  /** Where it went down — the reference for slop and for the tap decision. */
  readonly downX: number;
  readonly downY: number;
  readonly downAt: number;
  /** Where it was last seen. Re-baselined when a second pointer joins or leaves. */
  x: number;
  y: number;
  moved: boolean;
}

export function bindInput({ canvas, renderer, getState, ui, panel, hud, runCommand }: InputDeps): void {
  const pointers = new Map<number, Tracked>();
  /** Distance between two pointers when the current pinch started. */
  let pinchStart = 0;

  const localOf = (e: PointerEvent) => {
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const two = (): [Tracked, Tracked] | null => {
    if (pointers.size !== 2) return null;
    const [a, b] = [...pointers.values()];
    return a && b ? [a, b] : null;
  };

  /**
   * Hit radii in world units for the camera as it stands. A mouse keeps the
   * constants exactly — `min` is zero for a fine pointer, so the max is a no-op
   * and desktop hit-testing is unchanged.
   */
  const radii = () => {
    const v = renderer.getView();
    const min = coarsePointer() ? TOUCH_TARGET_R / (v.fit * v.zoom) : 0;
    return { node: Math.max(NODE_HIT_R, min), route: Math.max(ROUTE_HIT_R, min) };
  };

  const spread = ([a, b]: [Tracked, Tracked]): number => Math.hypot(a.x - b.x, a.y - b.y);
  const midpoint = ([a, b]: [Tracked, Tracked]) => ({
    x: (a.x + b.x) / 2,
    y: (a.y + b.y) / 2,
  });

  /** Everything the old click handler did, unchanged apart from its trigger. */
  function tapAt(local: { x: number; y: number }): void {
    const s = getState();
    const w = renderer.toWorld(local.x, local.y);
    // A finger produces no hover, so without this the rubber band from a
    // freshly tapped producer would run to wherever the mouse last was — the
    // world origin, on a device that has never had one.
    ui.mouse = w;
    const r = radii();

    const node = hitNode(s, w.x, w.y, r.node);
    if (node) {
      if (ui.linkFrom !== null) {
        if (node.id !== ui.linkFrom) {
          // A choice only where there is one: on an empty map no path over
          // built roads is cheaper than the straight one, so this is the old
          // one-tap build until the network has something to reuse.
          const options = viaOptions(s, ui.linkFrom, node.id);
          if (options.length > 0) {
            ui.linkTo = node.id;
            panel.invalidate();
            return;
          }
          const routeId = s.nextRouteId;
          ui.selection = runCommand({ type: "buildRoute", from: ui.linkFrom, to: node.id, good: ui.linkGood ?? undefined })
            ? { kind: "route", id: routeId } : null;
        }
        ui.linkFrom = null;
        ui.linkGood = null;
        ui.linkTo = null;
      } else if (node.kind === "producer" && node.rate > 0) {
        ui.linkFrom = node.id;
        ui.selection = { kind: "node", id: node.id };
      } else if (node.kind === "producer") {
        // A works the world has closed starts no road — buildRoute() refuses one
        // (RFC-0017 §3.5), and a link that can only end in nothing is worse than
        // no link. Selecting it shows the card, which says why.
        ui.selection = { kind: "node", id: node.id };
      } else {
        ui.selection = { kind: "node", id: node.id };
      }
      panel.invalidate();
      return;
    }

    // The road already selected is passed in, so tapping the same place again
    // steps to the next one under the finger instead of re-picking this one.
    const selected = ui.selection?.kind === "route" ? ui.selection.id : null;
    const routeId = hitRoute(s, w.x, w.y, r.route, selected);
    ui.linkFrom = null;
    // A stale good would silently mislabel the next route out of a hub.
    ui.linkGood = null;
    ui.linkTo = null;
    ui.selection = routeId !== null ? { kind: "route", id: routeId } : null;
    panel.invalidate();
  }

  // Wheel zooms on the desktop, anchored where the cursor is. The map holds
  // 19 nodes after the last milestone, and a small laptop screen has a milder
  // version of the crowding a phone has — the camera was already built.
  canvas.addEventListener("wheel", (e) => {
    e.preventDefault();
    const rect = canvas.getBoundingClientRect();
    const { vw, vh } = renderer.size();
    // deltaMode 1 is lines, not pixels; a trackpad reports pixels and a mouse
    // wheel often does not, and a factor of 16 puts them on the same footing.
    const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
    renderer.setView(
      zoomAround(
        renderer.getView(), vw, vh,
        e.clientX - rect.left, e.clientY - rect.top,
        Math.exp(-dy * 0.002), renderer.getWorldRect(),
      ),
    );
  }, { passive: false });

  canvas.addEventListener("pointerdown", (e) => {
    const local = localOf(e);
    pointers.set(e.pointerId, {
      downX: local.x, downY: local.y, downAt: e.timeStamp,
      x: local.x, y: local.y, moved: false,
    });
    // Capture keeps a gesture alive when the finger leaves the canvas, and is
    // an optimisation rather than a requirement — it throws for a pointer the
    // browser does not consider active. Tracking comes first so that a refused
    // capture costs the gesture nothing.
    try {
      canvas.setPointerCapture(e.pointerId);
    } catch {
      // no capture; the listeners on the canvas still see the gesture
    }

    const pair = two();
    if (pair) pinchStart = spread(pair);
  });

  canvas.addEventListener("pointermove", (e) => {
    const local = localOf(e);
    const p = pointers.get(e.pointerId);

    // Hover is a mouse concept. A finger that set it would leave a node lit up
    // after it is gone, and rubber-band the pending link to a stale position.
    if (e.pointerType === "mouse" && pointers.size === 0) {
      const s = getState();
      const w = renderer.toWorld(local.x, local.y);
      ui.mouse = w;
      const node = hitNode(s, w.x, w.y);
      ui.hover = node ? node.id : null;
      canvas.style.cursor = node || hitRoute(s, w.x, w.y) !== null ? "pointer" : "default";
      return;
    }

    if (!p) return;

    const prevX = p.x;
    const prevY = p.y;
    p.x = local.x;
    p.y = local.y;
    if (Math.hypot(local.x - p.downX, local.y - p.downY) > TAP_SLOP) p.moved = true;

    const pair = two();
    if (pair) {
      const now = spread(pair);
      if (pinchStart > 0 && now > 0) {
        const mid = midpoint(pair);
        const { vw, vh } = renderer.size();
        renderer.setView(
          zoomAround(renderer.getView(), vw, vh, mid.x, mid.y, now / pinchStart, renderer.getWorldRect()),
        );
        pinchStart = now;
      }
      // Both pointers count as moved: a pinch is never also a tap.
      for (const q of pointers.values()) q.moved = true;
      return;
    }

    if (!p.moved) return;

    const { vw, vh } = renderer.size();
    renderer.setView(panBy(renderer.getView(), vw, vh, local.x - prevX, local.y - prevY, renderer.getWorldRect()));
    // Panning while a link is pending has to stay possible — the target city may
    // be off-screen at zoom. The pan holds the world point under the finger, so
    // the rubber band stays anchored where the player put it and the map slides
    // beneath it, which is the reading that matches the gesture.
    if (ui.linkFrom !== null) ui.mouse = renderer.toWorld(local.x, local.y);
  });

  const release = (e: PointerEvent, cancelled: boolean): void => {
    const p = pointers.get(e.pointerId);
    pointers.delete(e.pointerId);
    try {
      if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
    } catch {
      // never captured; nothing to release
    }

    // Lifting one finger of a pinch must not snap the view: the survivor starts
    // a fresh baseline rather than inheriting the pair's.
    const pair = two();
    pinchStart = pair ? spread(pair) : 0;
    // Whatever is still down was part of a gesture; it cannot become a tap.
    for (const q of pointers.values()) q.moved = true;

    if (cancelled || !p || p.moved) return;
    if (e.timeStamp - p.downAt > TAP_TIME) return;
    tapAt(localOf(e));
  };

  canvas.addEventListener("pointerup", (e) => release(e, false));
  // A system gesture, an incoming call, a palm: the drag ends and selects
  // nothing. Without this the interrupted drag lands as a tap on whatever was
  // underneath it.
  canvas.addEventListener("pointercancel", (e) => release(e, true));

  canvas.addEventListener("pointerleave", (e) => {
    if (e.pointerType === "mouse") ui.hover = null;
  });

  document.addEventListener("keydown", (e) => {
    switch (e.key) {
      case "Escape":
        ui.linkFrom = null;
        ui.linkGood = null;
        ui.linkTo = null;
        ui.selection = null;
        panel.invalidate();
        break;
      case " ":
        e.preventDefault();
        hud.togglePause();
        break;
      case "l":
      case "L":
        if (ui.selection?.kind === "route") runCommand({ type: "buyTruck", routeId: ui.selection.id });
        break;
      case "t":
      case "T":
        hud.toggleTheme();
        break;
      case "1": case "2": case "4":
        hud.setSpeed(Number(e.key));
        break;
      default:
        break;
    }
  });

  // Sicherheitsnetz: verweist die Auswahl auf einen entfernten Knoten, aufräumen.
  window.addEventListener("blur", () => {
    if (ui.selection?.kind === "node") {
      try {
        findNode(getState(), ui.selection.id);
      } catch {
        ui.selection = null;
        panel.invalidate();
      }
    }
  });
}
